import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import type { Faker } from '@faker-js/faker'
import { customAlphabet, customRandom } from 'nanoid'

/**
 * Must match {@link Word.REG_EXP} (`/\w+/`)
 *
 * Useful when composing strings where you want to reserve special characters to
 * use as separators, indicators, etc.
 *
 * __NOTE:__ until TypeScript supports
 * {@link https://github.com/microsoft/TypeScript/issues/41160 RegExp types},
 * this is going to be typed as a `string`, but {@link Word.isWord} and
 * {@link Word.assertIsWord} will check against {@link Word.REG_EXP}
 */
export type Word = string
export namespace Word {
  /** `/\w+/` */
  export const REG_EXP = /\w+/

  export const isWord: TypeChecker<Word> = TypeChecker.Factory.custom(
    (val: any): val is Word => TypeChecker.isString(val) && REG_EXP.test(val),
    { typeDescription: `/${REG_EXP.source}/` },
  )
  export const assertIsWord: TypeAsserter<Word> =
    TypeAsserter.Factory.from(isWord)

  // Base 58 alphabet:
  // https://docs.neo.org/docs/en-us/basic/concept/cryptography/encode_algorithm.html
  const ALPHABET =
    '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz' satisfies Word
  // For evaluating collisions: https://zelark.github.io/nano-id-cc/
  const DEFAULT_LENGTH = 12
  const RAW_GENERATOR = customAlphabet(ALPHABET, DEFAULT_LENGTH)
  /**
   * Creates a
   * {@link https://docs.neo.org/docs/en-us/basic/concept/cryptography/encode_algorithm.html base-58}
   * encoded generated ID
   *
   * Unlikely to have collisions
   */
  export function generate(): Word {
    return RAW_GENERATOR()
  }
  export namespace generate {
    /**
     * Same as {@link generate} but the randomness is determined by the given
     * {@link Faker} instance
     */
    export function using(faker: Faker): Word {
      return customRandom(
        ALPHABET,
        DEFAULT_LENGTH,
        count =>
          new Uint8Array(
            faker.helpers.multiple(() => faker.number.int(255), { count }),
          ),
      )()
    }
  }
}
